



















































import Component from 'vue-class-component';
import { Vue, Prop, Watch } from 'vue-property-decorator'
import LineChart from '@/helpers/charts/LineChart';
import Chart from 'chart.js';
import { Base } from '@/base'
import 'chartjs-plugin-style';
import { formatData } from '@/constants/commonContent';

@Component({
  components: {
    LineChart,
  }
})
export default class ChartJs extends Base {
  @Prop({default: ''}) title;
  @Prop({default: () => ({})}) options: any;
  @Prop({default: () => ([])}) data: any;
  @Prop({default: () => ({})}) token: any;
  @Prop({default: () => ({fromToAction: () => ({})})}) actions: any;
  @Prop({default: () => false}) isXdai: boolean | undefined
  chartData: any = null;
  marketGradientStroke: any;
  priceGradientStroke: any;
  // Computed
  get priceData () {
    return this.getData('price');
  }
  get sharesData () {
    return this.getData('shares');
  }
  get processData () {
    let from = this.options.from
            ? this.$moment(this.options.from)
                    .startOf('day')
                    .valueOf()
            : null;
    let to = this.options.to
            ? this.$moment(this.options.to)
                    .endOf('day')
                    .valueOf()
            : null;
    let processData = this.data.reduce((acc: any[], item: any) => {
      const date = this.$moment.unix(item.processed_at).format(formatData.dateTime);
      acc.push(date);
      return acc;
    }, []);
    if (!from) {
      const from$: any | undefined = [...this.data].shift();
      if (from$) {
        // Set first date from results to filter
        Vue.set(this.options, 'from', new Date(from$.processed_at * 1000).toISOString());
      }
    }
    if (!to) {
      const to$: any = [...this.data].pop();
      if (to$) {
        // Set last date from results to filter
        Vue.set(this.options, 'to', new Date(to$.processed_at * 1000).toISOString());
      }
    }
    if (from) {
      // Set chart timeline start date (from filter)
      processData.unshift(from);
    }
    if (to) {
      // Set chart timeline end date (from filter)
      processData.push(to);
    }
    processData = processData.map(item =>
            typeof item === 'string'
                    ? this.$moment(item, formatData.dateTime).valueOf()
                    : this.$moment(item).valueOf()
    );
    return processData;
  }
  get from () {
    return this.$moment(this.options.from || Date.now()).format(formatData.date);
  }
  get to () {
    return this.$moment(this.options.to || Date.now()).format(formatData.date);
  }
  get priceGradientParams () {
    return {
      borderColor: this.priceGradientStroke,
      borderWidth: 2,
      pointBorderColor: this.priceGradientStroke,
      pointBackgroundColor: this.priceGradientStroke,
      pointHoverBackgroundColor: this.priceGradientStroke,
      pointHoverBorderColor: this.priceGradientStroke,
      pointBorderWidth: 1,
      pointHoverRadius: 3,
      pointHoverBorderWidth: 1,
      pointRadius: 1,
      fill: false,
      lineTension: 0
    };
  }
  get marketGradientParams () {
    return {
      borderColor: this.marketGradientStroke,
      borderWidth: 2,
      pointBorderColor: this.marketGradientStroke,
      pointBackgroundColor: this.marketGradientStroke,
      pointHoverBackgroundColor: this.marketGradientStroke,
      pointHoverBorderColor: this.marketGradientStroke,
      pointBorderWidth: 1,
      pointHoverRadius: 3,
      pointHoverBorderWidth: 1,
      pointRadius: 1,
      fill: false,
      lineTension: 0
    };
  }
  get chartOptions () {
    return {
      ...{
        legend: {
          labels: {
            usePointStyle: false,
            boxWidth: 30,
            fontColor: '#000',
            fontStyle: 'bold',
            fontSize: 11,
            fontFamily: 'Roboto, sans-serif',
            padding: 10
          }
        },
        scales: {
          yAxes: [
            {
              type: 'linear',
              zeroLineWidth: 0,
              display: true,
              position: 'left',
              id: 'y-axis-1',
              ticks: {
                callback: value => `${formatData.number(value, 7)}`,
                beginAtZero: true,
                fontColor: '#000',
                fontStyle: 'bold',
                lineHeight: 1,
                padding: 20
              },
              gridLines: {
                drawBorder: false,
                drawTicks: false,
                tickMarkLength: 20
              }
            },
            {
              type: 'linear',
              display: true,
              zeroLineWidth: 0,
              position: 'right',
              id: 'y-axis-2',
              ticks: {
                callback: value => `${formatData.number(value, 7)}`,
                beginAtZero: true,
                fontColor: '#000',
                fontStyle: 'bold',
                lineHeight: 1
              },
              gridLines: {
                display: false,
                drawBorder: false,
                tickMarkLength: 20
              }
            }
          ],
          xAxes: [
            {
              type: 'time',
              distribution: 'linear',
              time: {
                unit: 'day',
                displayFormats: {
                  month: 'MMM DD'
                },
                max: (() => this.$moment(this.options.to).valueOf())()
              },
              parser: time => this.$moment(time),
              zeroLineWidth: 0,
              gridLines: {
                display: false,
                drawBorder: false,
                tickMarkLength: 20
              }
            }
          ]
        }
      },
      ...this.options
    };
  }
  @Watch('data')
  onDataChanged(val: any, oldVal: any) {
    this.fillData()
  }
  mounted () {
    this.setGradient();
    const colors = ['#0138f4', '#08dac2'];
    Chart.plugins.register({
      beforeDraw: c => {
        this.fillLegends(c, colors);
      }
    });
    this.fillData();
  }
  destroyed () {
    this.options.from = null;
    this.options.to = null;
  }
  // Methods
  fillData () {
    const shadowColor = 'rgba(0, 0, 0, 0.2)';
    this.chartData = {
      labels: this.processData,
      datasets: [
        {
          backgroundColor: '#0138f4',
          label: `Shares (${this.token.symbol})`,
          data: this.sharesData,
          yAxisID: 'y-axis-1',
          shadowOffsetX: 0,
          shadowOffsetY: 7,
          shadowBlur: 15,
          shadowColor,
          ...this.marketGradientParams
        },
        {
          backgroundColor: '#08dac2',
          label: !this.isXdai ? 'Price (ETH)' : 'Price (USD)' ,
          data: this.priceData,
          yAxisID: 'y-axis-2',
          shadowOffsetX: 0,
          shadowOffsetY: 7,
          shadowBlur: 15,
          shadowColor,
          ...this.priceGradientParams
        }
      ]
    };
  }
  setGradient () {
    this.priceGradientStroke = '#08dac2';
    this.marketGradientStroke = '#0138f4';
  }
  fillLegends (c, colors) {
    const legends = c.legend.legendItems;
    legends.forEach((legend, i) => {
      legend.fillStyle = colors[i];
      legend.lineWidth = 0;
    });
  }
  getData (dataType) {
    return this.data.reduce((acc: any, item: any) => {
          acc.push({ y: item[dataType], t: item.processed_at * 1000 });
          return acc;
        }, [])
        .sort((a, b) => a.t - b.t);
  }
};
